import { useNotification } from "@context/NotificationContext";
import {
  claimLicenseById,
  claimPortOperatorLicenseById,
  exportVendorLicenseCsvById,
  unclaimLicenseById,
  unclaimPortOperatorLicenseById,
} from "@services/licensingApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { t } from "i18next";

export interface UseClaimLicenseProps {
  onClaimLicense: () => void;
}
export interface UseUnclaimLicenseProps {
  onUnclaimLicense: () => void;
}

export interface UseDownloadLicenseCsvProps {
  onDownloadLicenseCsv: () => void;
}

const CSV_MIME = "text/csv;charset=utf-8";

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;

  document.body.appendChild(a);
  a.click();
  a.remove();

  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

const toCsvBlob = async (res: unknown): Promise<Blob> => {
  const payload: any = (res as any)?.data ?? res;

  if (payload instanceof Blob) {
    return payload.type ? payload : new Blob([payload], { type: CSV_MIME });
  }

  if (typeof payload === "string") {
    return new Blob([payload], { type: CSV_MIME });
  }

  if (payload instanceof ArrayBuffer) {
    return new Blob([payload], { type: CSV_MIME });
  }

  if (payload && typeof payload === "object" && ArrayBuffer.isView(payload)) {
    const view = payload as ArrayBufferView;
    const u8 = new Uint8Array(view.buffer as any, view.byteOffset, view.byteLength);

    const copy = new Uint8Array(u8.byteLength);
    copy.set(u8);

    return new Blob([copy.buffer], { type: CSV_MIME });
  }

  if (payload && typeof payload.text === "function") {
    const text = await payload.text();
    return new Blob([text], { type: CSV_MIME });
  }

  return new Blob([String(payload ?? "")], { type: CSV_MIME });
};

export const useClaimLicense = (
  licenseId: string | undefined,
  refetchLicense: () => Promise<unknown> | void,
  isPortOperatorContext = false,
): UseClaimLicenseProps => {
  const { notify } = useNotification();

  const claimMutation = useMutation({
    mutationFn: async () => {
      if (!licenseId) return;

      return runTransaction(
        {
          module: "licensing",
          screen: "LicenseDetails",
          action: isPortOperatorContext ? "claim_port_operator_license" : "claim_vendor_license",
          entityType: "license_request",
        },
        (transactionId) =>
          isPortOperatorContext
            ? claimPortOperatorLicenseById({
                id: licenseId,
                ...(transactionId ? { transactionId } : {}),
              })
            : claimLicenseById({ id: licenseId, ...(transactionId ? { transactionId } : {}) }),
      );
    },
    onSuccess: async () => {
      await refetchLicense();
      notify({
        title: t("licensing.notifications.license-claimed"),
        type: "success",
      });
    },
    onError: (error: Error) => {
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const onClaimLicense = () => claimMutation.mutate();
  return { onClaimLicense };
};

export const useUnclaimLicense = (
  licenseId: string | undefined,
  refetchLicense: () => Promise<unknown> | void,
  isPortOperatorContext = false,
): UseUnclaimLicenseProps => {
  const { notify } = useNotification();

  const unclaimMutation = useMutation({
    mutationFn: async () => {
      if (!licenseId) return;

      return runTransaction(
        {
          module: "licensing",
          screen: "LicenseDetails",
          action: isPortOperatorContext
            ? "unclaim_port_operator_license"
            : "unclaim_vendor_license",
          entityType: "license_request",
        },
        (transactionId) =>
          isPortOperatorContext
            ? unclaimPortOperatorLicenseById({
                id: licenseId,
                ...(transactionId ? { transactionId } : {}),
              })
            : unclaimLicenseById({ id: licenseId, ...(transactionId ? { transactionId } : {}) }),
      );
    },
    onSuccess: async () => {
      await refetchLicense();
      notify({
        title: t("licensing.notifications.license-unclaimed"),
        type: "success",
      });
    },
    onError: (error: Error) => {
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const onUnclaimLicense = () => unclaimMutation.mutate();
  return { onUnclaimLicense };
};

export const useDownloadLicenseCsv = (
  licenseId: string | undefined,
): UseDownloadLicenseCsvProps => {
  const { notify } = useNotification();

  const downloadCsvMutation = useMutation({
    mutationFn: async () => {
      if (!licenseId) return null;
      const res = await runTransaction(
        {
          module: "licensing",
          screen: "LicenseDetails",
          action: "export_vendor_license_csv",
          entityType: "license_report",
        },
        (transactionId) =>
          exportVendorLicenseCsvById({
            id: licenseId,
            ...(transactionId ? { transactionId } : {}),
          }),
      );
      return toCsvBlob(res);
    },
    onSuccess: (blob) => {
      if (!licenseId || !blob) return;

      if (blob.size === 0) {
        notify({
          title: t("common.error"),
          description: t("common.something-went-wrong"),
          type: "error",
        });
        return;
      }

      downloadBlob(blob, `MarHub-Access-${licenseId}.csv`);

      notify({
        title: t("licensing.notifications.vendor-license-csv-download-success-title"),
        description: t("licensing.notifications.vendor-license-csv-download-success-description"),
        type: "success",
      });
    },
    onError: (error: any) => {
      const detail =
        error?.response?.data?.detail ?? error?.data?.detail ?? error?.detail ?? error?.message;

      notify({
        title: t("common.error"),
        description:
          typeof detail === "string" && detail.length > 0
            ? detail
            : t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  return {
    onDownloadLicenseCsv: () => downloadCsvMutation.mutate(),
  };
};
